import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import axios from 'axios';
import { Megaphone, Pencil, ExternalLink } from 'lucide-react';

import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  getAnnouncement, publishAnnouncement, updateAnnouncement, buildPublishPayload, markAnnouncementOpened,
} from './config';
import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import CheckboxInput from '../../components/ui/CheckboxInput';
import AssignmentTargetPicker, { EMPTY_TARGETS } from '../../components/ui/AssignmentTargetPicker';
import PictureUpload from '../../components/ui/PictureUpload';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import AnnouncementReadsTab from './tab-pages/AnnouncementReadsTab';

const Page = styled.div`
  padding: 16px;
`;
// Yönetici + yayımlanmış duyuru: Genel / Okunma sekmeleri. Tek kaydırma — sayfa taşmaz; Genel kendi alanında, Okunma
// yalnız DataTable satırlarında kayar.
const TabbedPage = styled.div`
  padding: 16px 16px 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;
const TabHeader = styled.div`
  flex-shrink: 0;
`;
const TabBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;
const ScrollArea = styled.div`
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 16px;
`;
const Layout = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 24px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
const HeaderBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`;
const TitleBlock = styled.div`
  flex: 1;
`;
const ActionBar = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;
const ImageColumn = styled.div``;
const ImageBox = styled.div`
  width: 100%;
  height: 240px;
  border-radius: 12px;
  background: ${({ theme, $hasImage }) =>
    $hasImage
      ? '#000'
      : `linear-gradient(135deg, ${theme.colors.primary.main}22 0%, ${theme.colors.secondary.main}22 100%)`};
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
  margin-bottom: 12px;
  position: relative;
`;
const Img = styled.img`
  max-width: 100%; max-height: 100%; object-fit: cover; width: 100%; height: 100%;
`;
const PlaceholderIcon = styled.div`
  display: flex; align-items: center; justify-content: center;
  width: 96px; height: 96px;
  border-radius: 48px;
  background: #FFFFFF;
  box-shadow: 0 6px 20px rgba(0, 66, 66, 0.08);
`;

const InfoColumn = styled.div``;
const Title = styled.h2`
  margin: 0 0 8px 0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  color: ${({ theme }) => theme.colors.text.primary};
`;
const Meta = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 16px;
`;
const Description = styled.div`
  white-space: pre-wrap;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`;
const Actions = styled.div`
  display: flex; gap: 12px; flex-wrap: wrap; margin-top: 8px;
`;
// Yayın çekmecesinin gövdesi (GenericFormDrawer kabuğu, schema={[]} + children).
const PublishBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;
const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  background: ${({ published, theme }) => (published ? theme.colors.secondary.light : theme.colors.bgColor)};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-left: 8px;
`;

export default function AnnouncementDetailsPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [targets, setTargets] = useState({ ...EMPTY_TARGETS });
  const [sendNotification, setSendNotification] = useState(true);
  const [sendEmail, setSendEmail] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [, setUploading] = useState(false);
  // Açılma kaydı duyuru başına BİR kez (yeniden yüklemeler — düzenleme, görsel — ikinci istek atmaz).
  const openedFor = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    getAnnouncement(id)
      .then((r) => setItem(r.data?.data || null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // "Açtı" = detayın açılması (AKM POC §8). Yalnız yayımlanmış duyuruda; sunucu hedef dışını kaydetmez.
  useEffect(() => {
    if (!item || item.status !== 'published' || openedFor.current === id) return;
    openedFor.current = id;
    markAnnouncementOpened(id);
  }, [id, item]);

  const handleEditSave = useCallback(async (formData) => {
    const ok = await updateAnnouncement(id, formData)
      .then(() => true)
      .catch(() => false);
    if (ok) { setEditOpen(false); load(); }
    return ok;
  }, [id, load]);

  const handleImageChange = useCallback(async (file) => {
    setUploading(true);
    try {
      let imageUrl = null;
      if (file) {
        const presign = await api.post(
          `/v1/storage/presign?fileName=${encodeURIComponent(file.name)}&mimeType=${encodeURIComponent(file.type)}&purpose=${encodeURIComponent('announcementImage')}`
        );
        const { upload_url, file_url } = presign.data || {};
        if (!upload_url) return;
        await axios.put(upload_url, file, { headers: { 'Content-Type': file.type } });
        imageUrl = file_url;
      }
      await updateAnnouncement(id, {
        title: item.title,
        description: item.description,
        detailUrl: item.detailUrl,
        expiryDate: item.expiryDate,
        imageUrl,
      });
      load();
    } finally {
      setUploading(false);
    }
  }, [id, item, load]);

  // GenericFormDrawer onSave sözleşmesi: başarıda true (çekmece kapanır), hatada false (açık kalır; toast interceptor'da).
  const handlePublish = useCallback(async () => {
    try {
      await publishAnnouncement(id, buildPublishPayload(targets, { sendNotification, sendEmail }));
      // Yayınlayan kişi yayından hemen sonra "açtı" sayılmasın (inceleme I6): yeniden yükleme açılma kaydı atmaz;
      // sayfaya sonradan yeniden girerse (yeni bağlama) açılış normal sayılır.
      openedFor.current = id;
      load();
      return true;
    } catch {
      return false;
    }
  }, [id, targets, sendNotification, sendEmail, load]);

  const editSchema = [
    { name: 'title', label: t('announcement.title'), type: 'text', grid: 12, required: true },
    { name: 'description', label: t('announcement.description'), type: 'textarea', grid: 12, required: true },
    { name: 'detailUrl', label: t('announcement.detailUrl'), type: 'text', grid: 12 },
    { name: 'expiryDate', label: t('announcement.expiryDate'), type: 'date', grid: 12 },
  ];

  if (loading || !item) return null;

  const isPublished = item.status === 'published';
  const isAdmin = canWrite('AN');

  const breadcrumbs = (
    <Breadcrumbs items={[
      { label: t('announcement.pageTitle'), path: isAdmin ? '/announcements' : '/my-announcements' },
      { label: item.title },
    ]} />
  );

  const general = (
    <Layout>
      <ImageColumn>
        {isAdmin ? (
          <PictureUpload
            value={item.imageUrl}
            onChange={handleImageChange}
            shape="square"
            accept="image/*"
            label={t('announcement.imageUrl')}
          />
        ) : (
          <ImageBox $hasImage={!!item.imageUrl}>
            {item.imageUrl
              ? <Img src={item.imageUrl} alt={item.title} />
              : <PlaceholderIcon><Megaphone size={48} color="#0291EF" /></PlaceholderIcon>}
          </ImageBox>
        )}
      </ImageColumn>

      <InfoColumn>
        <HeaderBar>
          <TitleBlock>
            <Title>
              {item.title}
              <StatusBadge published={isPublished}>{t(`announcement.statusValues.${item.status}`)}</StatusBadge>
            </Title>
            <Meta>
              {item.publishedAt && <>{t('announcement.publishedAt')}: {new Date(item.publishedAt).toLocaleString()}<br/></>}
              {item.expiryDate && <>{t('announcement.expiryDate')}: {new Date(item.expiryDate).toLocaleDateString()}{item.isExpired ? ` (${t('announcement.expired')})` : ''}</>}
            </Meta>
          </TitleBlock>
          <ActionBar>
            {item.detailUrl && (
              <IconButton
                type="button"
                onClick={() => window.open(item.detailUrl, '_blank')}
                title={t('announcement.openDetail')}
              >
                <ExternalLink size={16} />
              </IconButton>
            )}
            {isAdmin && (
              <IconButton
                type="button"
                onClick={() => setEditOpen(true)}
                title={t('common.edit')}
              >
                <Pencil size={16} />
              </IconButton>
            )}
          </ActionBar>
        </HeaderBar>
        <Description>{item.description}</Description>
        {isAdmin && !isPublished && (
          <Actions>
            <Button type="primary" onClick={() => setPublishOpen(true)}>{t('announcement.publish')}</Button>
          </Actions>
        )}
      </InfoColumn>
    </Layout>
  );

  const drawers = (
    <>
      <GenericFormDrawer
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={t('announcement.edit')}
        schema={editSchema}
        initialValues={item}
        onSave={handleEditSave}
      />

      <GenericFormDrawer
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        title={t('announcement.publish')}
        submitLabel={t('announcement.publish')}
        schema={[]}
        onSave={handlePublish}
      >
        <PublishBody>
          {/* Personel statüsü hedefi açık: yayın ucu employeeStatusIds + excludes.employeeStatusIds taşır. */}
          <AssignmentTargetPicker value={targets} onChange={setTargets} showEmployeeStatus />
          <CheckboxInput
            label={t('announcement.sendNotification')}
            checked={sendNotification}
            onChange={(val) => setSendNotification(typeof val === 'boolean' ? val : val?.target?.checked)}
          />
          <CheckboxInput
            label={t('announcement.sendEmail')}
            checked={sendEmail}
            onChange={(val) => setSendEmail(typeof val === 'boolean' ? val : val?.target?.checked)}
          />
        </PublishBody>
      </GenericFormDrawer>
    </>
  );

  // Okunma sekmesi yalnız duyuru yöneticisine ve yayımlanmış duyuruda (AKM POC §8); diğer herkes bugünkü sekmesiz görünüm.
  if (!(isAdmin && isPublished)) {
    return (
      <Page>
        {breadcrumbs}
        {general}
        {drawers}
      </Page>
    );
  }

  return (
    <TabbedPage>
      <TabHeader>
        {breadcrumbs}
        <Tabs
          tabs={[
            { key: 'general', label: t('announcement.tabs.general') },
            { key: 'reads', label: t('announcement.tabs.reads') },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
        />
      </TabHeader>
      <TabBody>
        {activeTab === 'reads'
          ? <AnnouncementReadsTab announcementId={id} trackingEnabled={!!item.openTrackingEnabled} />
          : <ScrollArea>{general}</ScrollArea>}
      </TabBody>
      {drawers}
    </TabbedPage>
  );
}
